// combatpro/panels/monsters/studio/useMonsterStudioPersistence.js
//
// Combat PRO - Monster Studio persistence runtime (Stage 58: Persistence & CRUD).
//
// The persistence layer, expressed as a React hook that coordinates the three
// established runtimes with the backend. It OWNS backend communication, CRUD, and
// version history - and nothing else. It consumes:
//   - the Stage 55 engine    (cloneStudioMonster / nextMonsterId / deriveSaveStatus
//                             / validateStudioMonster) for all model transforms and
//                             validation - none of which it recreates;
//   - the Stage 56 editor     (editor.model / loadModel / setActiveTab / setDirty)
//                             as the single owner of the working model + dirty flag;
//   - the Stage 57 library    (library.customMonsters / setCustomMonsters) as the
//                             single owner of the custom pool - populated here, never
//                             bypassed;
//   - the monsterPersistence  API module for the actual cloud calls.
//
// CombatContext is untouched. No editing logic, no list/filter logic, and no model
// math is reimplemented here.
//
// Operations are faithful ports of the legacy Monster Studio (battletracker.html):
//   boot load   <- viStudioLoadLibrary 10944   save     <- viStudioSave 11240
//   save as     <- viStudioSaveAs (clone+save)  duplicate<- viStudioDuplicate (clone)
//   delete      <- viStudioDelete (confirm)      favourite<- viStudioFavourite
//   archive     <- viStudioArchive               restore  <- viStudioReset 11279
//   versions    <- viStudioVersionRestore/Duplicate/Delete 11493-11518
//
// Decision (noted in report): legacy viStudioSave clears dirty + writes the pool
// optimistically BEFORE awaiting the POST, leaving the user "clean" even if the
// network call failed. This port performs the POST first and only commits the pool
// update + dirty-clear on success (the brief's "update library immediately after
// successful save"), which fixes that edge case while preserving all other
// behaviour. Save As / Duplicate / version-duplicate intentionally remain unsaved
// (dirty) exactly as the legacy left them.
//
// No DOM access beyond window.confirm for the legacy delete/restore confirmations
// (the brief requires legacy confirmation behaviour). No localStorage. No optional
// chaining. No em dash.

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  cloneStudioMonster,
  nextMonsterId,
  deriveSaveStatus,
  validateStudioMonster
} from '../../../monster/monsterStudioEngine';
import {
  loadUserMonsters,
  saveUserMonster,
  deleteUserMonster
} from '../../../monster/monsterPersistence';

// ---- Pure persistence helpers (version history belongs to this layer) -------

function buildVersionHistory(prior, now) {
  var hist = Array.isArray(prior.versions) ? prior.versions.slice() : [];
  var snap = cloneStudioMonster(prior);
  if (snap.versions) { delete snap.versions; }
  hist.push({ ts: (prior.lastSaved || now), name: (prior.customName || prior.name || ''), snapshot: snap });
  if (hist.length > 10) { hist = hist.slice(-10); }
  return hist;
}

// Build the record to persist from the current editor model (viStudioSave body).
// Pure: returns a fresh model; the live editor model is not mutated here.
function prepareSaveModel(model, pool, asNew, now) {
  var m = cloneStudioMonster(model);
  if (asNew) {
    var nid = nextMonsterId(pool, 0).id;
    m.customMonsterId = nid;
    m.id = nid;
    m.versions = [];
    m.lastSaved = null;
  }
  if (!m.customMonsterId) {
    m.customMonsterId = nextMonsterId(pool, 0).id;
  }
  m.id = m.customMonsterId;
  m.status = deriveSaveStatus(m);
  var idx = pool.findIndex(function(x) { return (x.customMonsterId || x.id) === m.customMonsterId; });
  if (idx >= 0) {
    m.versions = buildVersionHistory(pool[idx], now);
  } else if (!Array.isArray(m.versions)) {
    m.versions = [];
  }
  m.lastSaved = now;
  return m;
}

// Replace-or-append a saved model into a pool array (immutable).
function commitToPool(pool, saved) {
  var idx = pool.findIndex(function(x) { return (x.customMonsterId || x.id) === saved.customMonsterId; });
  var next = pool.slice();
  if (idx >= 0) { next[idx] = cloneStudioMonster(saved); }
  else { next.push(cloneStudioMonster(saved)); }
  return next;
}

// ---- Hook ------------------------------------------------------------------

function useMonsterStudioPersistence(props) {
  const editor = props.editor;
  const library = props.library;

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const messageIdRef = useRef(0);
  const [messageId, setMessageId] = useState(0);
  const [versionsOpen, setVersionsOpen] = useState(false);

  function toast(text) {
    messageIdRef.current = messageIdRef.current + 1;
    setMessage(text);
    setMessageId(messageIdRef.current);
  }

  // Boot: load the custom pool once and hand it to the library runtime. The library
  // owns the pool; this only populates it.
  const setCustomMonsters = library.setCustomMonsters;
  useEffect(function() {
    var cancelled = false;
    (async function() {
      var models = await loadUserMonsters();
      if (!cancelled && Array.isArray(models)) {
        setCustomMonsters(models);
      }
    })();
    return function() { cancelled = true; };
  }, [setCustomMonsters]);

  // Core save (viStudioSave). asNew is handled by save-as/duplicate paths that
  // pre-clone; this function persists the current editor model.
  const save = useCallback(async function save(asNew) {
    var model = editor.model;
    if (!model) { return false; }
    var check = validateStudioMonster(model);
    if (!check.valid) {
      editor.setActiveTab('identity');
      toast(check.error);
      return false;
    }
    var pool = library.customMonsters;
    var now = Date.now();
    var saved = prepareSaveModel(model, pool, asNew, now);

    setBusy(true);
    var ok = await saveUserMonster(saved);
    setBusy(false);
    if (!ok) {
      toast('Save failed - check your connection and try again.');
      return false;
    }
    // Commit only on success: update the library pool, load the saved model back
    // into the editor (which clears dirty), and confirm.
    library.setCustomMonsters(function(prev) { return commitToPool(prev, saved); });
    editor.loadModel(saved);
    toast('Saved to cloud library');
    return true;
  }, [editor, library]);

  // Save As (viStudioSaveAs): clone current with a new identity, load it, save it.
  const saveAs = useCallback(async function saveAs() {
    var model = editor.model;
    if (!model) { return false; }
    var pool = library.customMonsters;
    var clone = cloneStudioMonster(model);
    var nid = nextMonsterId(pool, 0).id;
    clone.customMonsterId = nid;
    clone.id = nid;
    clone.versions = [];
    clone.customName = (clone.customName || clone.name || 'Monster') + ' (Copy)';
    clone.sourceMonster = model.sourceMonster || model.customName || null;
    clone.status = 'custom';
    clone.lastSaved = null;
    editor.loadModel(clone);
    return save(true);
  }, [editor, library, save]);

  // Duplicate (viStudioDuplicate): clone with a new identity into the editor but do
  // NOT save - it stays dirty until the user saves.
  const duplicate = useCallback(function duplicate() {
    var model = editor.model;
    if (!model) { return; }
    var pool = library.customMonsters;
    var clone = cloneStudioMonster(model);
    var nid = nextMonsterId(pool, 0).id;
    clone.customMonsterId = nid;
    clone.id = nid;
    clone.versions = [];
    clone.customName = (clone.customName || clone.name || 'Monster') + ' (Copy)';
    clone.sourceMonster = model.sourceMonster || model.customName || null;
    clone.status = 'custom';
    clone.lastSaved = null;
    editor.setModelDirty(clone);
    toast('Duplicated - save to keep it');
  }, [editor, library]);

  // Favourite toggle (viStudioFavourite): flip on the current model, then save.
  const favourite = useCallback(async function favourite() {
    var model = editor.model;
    if (!model) { return; }
    var next = cloneStudioMonster(model);
    next.favourite = !next.favourite;
    editor.setModelDirty(next);
    toast(next.favourite ? 'Marked as favourite' : 'Removed from favourites');
    await save(false);
  }, [editor, save]);

  // Archive (viStudioArchive): set archived, then save. Archived monsters drop out
  // of the active library sections and surface under Archived.
  const archive = useCallback(async function archive() {
    var model = editor.model;
    if (!model) { return; }
    var next = cloneStudioMonster(model);
    next.archived = true;
    editor.setModelDirty(next);
    toast('Archived');
    await save(false);
  }, [editor, save]);

  // Restore (viStudioArchive companion): clear archived, then save. Returns the
  // monster to the active library.
  const restore = useCallback(async function restore() {
    var model = editor.model;
    if (!model) { return; }
    var next = cloneStudioMonster(model);
    next.archived = false;
    editor.setModelDirty(next);
    toast('Restored');
    await save(false);
  }, [editor, save]);

  // Delete (viStudioDelete): confirm, remove from pool + backend, reset the editor.
  const remove = useCallback(async function remove() {
    var model = editor.model;
    if (!model || !model.customMonsterId) { return false; }
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Delete this monster from your cloud library? This cannot be undone.')) {
      return false;
    }
    var id = model.customMonsterId;
    setBusy(true);
    var ok = await deleteUserMonster(id);
    setBusy(false);
    if (!ok) {
      toast('Delete failed - check your connection and try again.');
      return false;
    }
    library.setCustomMonsters(function(prev) {
      return prev.filter(function(m) { return (m.customMonsterId || m.id) !== id; });
    });
    editor.newMonster();
    toast('Deleted');
    return true;
  }, [editor, library]);

  // ---- Version history (viStudioVersion*) ----------------------------------

  function openVersions() { setVersionsOpen(true); }
  function closeVersions() { setVersionsOpen(false); }

  // Restore a snapshot as the active monster, keeping id + version chain, then save
  // (which itself snapshots the pre-restore state). viStudioVersionRestore.
  const versionRestore = useCallback(async function versionRestore(i) {
    var model = editor.model;
    if (!model || !Array.isArray(model.versions) || !model.versions[i]) { return; }
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Restore this version as the active monster? The current state is saved as a new version first.')) {
      return;
    }
    var snap = cloneStudioMonster(model.versions[i].snapshot);
    snap.customMonsterId = model.customMonsterId;
    snap.id = model.customMonsterId;
    snap.versions = model.versions.slice();
    editor.setModelDirty(snap);
    setVersionsOpen(false);
    await save(false);
    toast('Version restored');
  }, [editor, save]);

  // Duplicate a snapshot as a new unsaved monster. viStudioVersionDuplicate.
  const versionDuplicate = useCallback(function versionDuplicate(i) {
    var model = editor.model;
    if (!model || !Array.isArray(model.versions) || !model.versions[i]) { return; }
    var pool = library.customMonsters;
    var snap = cloneStudioMonster(model.versions[i].snapshot);
    var nid = nextMonsterId(pool, 0).id;
    snap.customMonsterId = nid;
    snap.id = nid;
    snap.versions = [];
    snap.lastSaved = null;
    snap.customName = (snap.customName || snap.name || 'Monster') + ' (v' + (i + 1) + ')';
    editor.setModelDirty(snap);
    setVersionsOpen(false);
    toast('Version duplicated - save to keep it');
  }, [editor, library]);

  // Delete a single stored version, then persist the trimmed chain on the pooled
  // record. viStudioVersionDelete.
  const versionDelete = useCallback(async function versionDelete(i) {
    var model = editor.model;
    if (!model || !Array.isArray(model.versions)) { return; }
    var nextVersions = model.versions.slice();
    nextVersions.splice(i, 1);
    var next = cloneStudioMonster(model);
    next.versions = nextVersions;
    editor.setModel(next);
    var idx = library.customMonsters.findIndex(function(m) { return (m.customMonsterId || m.id) === next.customMonsterId; });
    if (idx >= 0) {
      library.setCustomMonsters(function(prev) { return commitToPool(prev, next); });
      await saveUserMonster(next);
    }
    toast('Version deleted');
  }, [editor, library]);

  return {
    busy: busy,
    message: message,
    messageId: messageId,
    versionsOpen: versionsOpen,
    save: function() { return save(false); },
    saveAs: saveAs,
    duplicate: duplicate,
    favourite: favourite,
    archive: archive,
    restore: restore,
    remove: remove,
    openVersions: openVersions,
    closeVersions: closeVersions,
    versionRestore: versionRestore,
    versionDuplicate: versionDuplicate,
    versionDelete: versionDelete
  };
}

export default useMonsterStudioPersistence;
